import FeedbackThumbs from "@components/FeedbackThumbs";
import { getTheme, Themes } from "@components/Header/ThemeSelector";
import { CpuChipIcon, UserCircleIcon } from "@heroicons/react/16/solid";
import { ChatMessageDTO } from "@onetutor/shared/dto";
import { MessageEnum } from "@onetutor/shared/type";
import { useRequiredParams } from "@utils/params";
import { useSearchParams } from "react-router";
import Typewriter from "typewriter-effect";
import { MarkdownContent } from "../../../components/Markdown";
import SourceCarousel from "./SourceCarousel";

interface ChatBubbleProps {
  message: ChatMessageDTO;
  messageArray: ChatMessageDTO[];
  citations: string[];
  isStreaming?: boolean;
  isWaitingForFirstToken?: boolean;
}

export function ChatBubble({
  message,
  messageArray,
  citations,
  isStreaming = false,
  isWaitingForFirstToken = false,
}: ChatBubbleProps) {
  const { courseId } = useRequiredParams(["courseId"]);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");

  if (!sessionId) {
    return null;
  }

  const isUser = message.author === MessageEnum.HUMAN;
  const latestMsg = messageArray[messageArray.length - 1];
  const isLatestAI =
    latestMsg.author === MessageEnum.AI && latestMsg.id === message.id;
  const isFirst = messageArray[0].id === message.id;

  const Icon = isUser ? UserCircleIcon : CpuChipIcon;

  const shouldShowTypewriter =
    !isUser && message.content === "" && isWaitingForFirstToken;

  const streamCursor =
    isStreaming && !isUser && !shouldShowTypewriter
      ? getTheme() === Themes.light
        ? " ⚫"
        : " ⚪️"
      : "";

  return (
    <div
      key={`KEY-${message.id}`}
      className={`chat ${isUser ? "chat-end" : "chat-start p-0"}`}
    >
      <div className="chat-image avatar">
        <div className="hidden sm:w-10">
          <Icon className="hidden sm:block" />
        </div>
      </div>
      <div
        className={`chat-bubble prose-markdown break-words rounded-xl text-[14px] shadow-sm sm:text-[16px] ${isUser ? "chat-bubble-primary text-primary-content" : "chat-bubble-neutral"} ${shouldShowTypewriter ? "h-[38px] w-12 sm:h-[43px]" : "my-1 w-auto py-[1px]"}`}
      >
        {shouldShowTypewriter ? (
          <Typewriter
            onInit={(typewriter) => {
              typewriter
                .pauseFor(200)
                .typeString("...")
                .pauseFor(400)
                .deleteChars(3)
                .start();
            }}
            options={{
              delay: 70,
              deleteSpeed: 50,
              skipAddStyles: true,
              cursorClassName: "hidden",
              loop: true,
            }}
          />
        ) : (
          <MarkdownContent
            components={{
              table: ({ node, ...props }) => (
                <div className="table-container">
                  <table {...props} />
                </div>
              ),
            }}
          >
            {`${message.content}${streamCursor}`}
          </MarkdownContent>
        )}
        {isLatestAI && citations.length > 0 && (
          <SourceCarousel courseId={courseId} citations={citations} />
        )}
        {!isUser && !isFirst && isLatestAI && !isStreaming && (
          <div className="-bottom-9 absolute left-0 group-hover:opacity-100">
            <FeedbackThumbs
              feedbackType="message"
              courseId={courseId}
              id={sessionId}
            />
          </div>
        )}
      </div>
    </div>
  );
}
